/**
 * CorpusMeter — where this team lands among real 2026 auction teams, drawn as
 * the corpus itself.
 *
 * Ben asked for "an animated meter that shows where your team stands against
 * all other teams in the 2026 corpus for similar leagues". What this renders is
 * a DISTRIBUTION STRIP rather than a speedometer: a compact histogram of the
 * format cell's real values, with the user's team marked on it.
 *
 * WHY A HISTOGRAM AND NOT A GAUGE. The bars ARE the corpus. A gauge graphic is
 * a picture of a number and could be drawn identically whatever the data looked
 * like; this shows the actual shape of real teams, so a reader can see that the
 * middle is crowded and the tails are thin without being told. It is honest by
 * construction and it is ours: nobody else grading a fantasy roster has 690
 * real auction teams to draw.
 *
 * 🔴 THE MARKER SITS ON THE VALUE AXIS, NOT THE PERCENTILE AXIS, AND THAT IS
 * NOT A DETAIL. The bars are bucketed by VALUE from the cell's minimum to its
 * maximum. Placing the marker at "62% across" would put it somewhere the bars
 * do not agree with, because a percentile is a rank and the axis under it is a
 * quantity. The number displayed is the percentile; the position drawn is the
 * value. Anything else is a chart that disagrees with its own axis.
 *
 * 🔴 EXTREMES ARE WORDS, NOT 100 AND 0. The top of the corpus reads "beats all
 * 690", the bottom "trails all 690". Nothing here ever prints "100th
 * percentile" or "0th percentile": one reads as a perfect score the grade does
 * not claim, and the other reads as a failure when it means "last of the real
 * teams we happened to measure", which is a much smaller statement.
 *
 * ANIMATION: CSS transitions for the sweep and the bars, one rAF loop for the
 * count-up, and nothing else. No animation library, once per mount, and
 * prefers-reduced-motion renders the finished state with no movement at all.
 *
 * 🔴 THE WHOLE METER CLIMBS TOGETHER. Ben: "show on the bar graph climbing
 * slowly also." The bars grow from the baseline, staggered left to right, over
 * the same window the marker crosses in, so what a reader watches is the corpus
 * building and their team walking into it - one movement, not a chart that
 * appears and then gets a marker stuck on it. The bars scale on
 * transform-origin bottom rather than animating height, so the browser never
 * relayouts the strip mid-sweep.
 *
 * 🔴 EVERY TIMING IS A METER_LOOK DIAL (src/data/meterLook.js), never a literal
 * in this file. Same contract as CLIP_LOOK and for the same reason: how slow is
 * slow enough is a judgement Ben makes looking at the page.
 */

import { useEffect, useState } from "react";
import useCountUp from "../../hooks/useCountUp.js";
import { METER_LOOK } from "../../data/meterLook.js";
import { prefersReducedMotion } from "../LiveBoard/NominationFlash.jsx";
import { C, SZ } from "../Submit/submitStyles.js";
import { ordinal } from "../../utils/ordinal.js";

/**
 * Bars across the strip: enough shape to read, few enough to stay legible on a
 * phone.
 *
 * ⚠️ NOT a METER_LOOK dial, deliberately. That file is the meter's MOTION;
 * this number changes the histogram itself - how finely the corpus is bucketed
 * and therefore what shape a reader sees - so it belongs beside the function
 * that does the bucketing. It does have one obligation to METER_LOOK: the
 * stagger arithmetic documented there assumes this count.
 */
export const METER_BARS = 30;

/**
 * A percentile as the copy prints it, never rounded INTO an extreme.
 *
 * 99.7% of the corpus beaten is not "100%", and 0.3% is not "0%": rounding a
 * near miss into an absolute claim is exactly the quiet overstatement the rest
 * of this page is built to avoid. A true extreme is handled by percentileClaim
 * below, in words.
 */
export function pctShown(pct) {
  const r = Math.round(pct);
  if (r >= 100 && pct < 100) return 99;
  if (r <= 0 && pct > 0) return 1;
  return r;
}

/**
 * A population count as the copy prints it: 690, and 1,240 rather than 1240.
 *
 * The bench cells are the first arrays on this page big enough to need it, and
 * a four-digit run of bare digits inside a sentence reads as an id rather than
 * as a count of real players.
 */
export function fmtCount(n) {
  return Number(n).toLocaleString("en-US");
}

/**
 * "beats 62% of 690 real superflex teams", or the extremes in words.
 * Null when there is no percentile or no corpus: no number, no claim.
 */
export function percentileClaim(pct, n, words) {
  if (pct == null || !Number.isFinite(pct) || !n) return null;
  if (pct >= 100) return `beats all ${fmtCount(n)} real ${words} teams`;
  if (pct <= 0) return `trails all ${fmtCount(n)} real ${words} teams`;
  return `beats ${pctShown(pct)}% of ${fmtCount(n)} real ${words} teams`;
}

/**
 * 1st, 2nd, 3rd, 4th, and the 11th-13th exception that every naive version of
 * this gets wrong. Moved to utils/ordinal.js on Sep 2 2026 so the room report's
 * rank badges could reach it without a component import; re-exported here
 * because this is where every percentile line already looks for it.
 */
export { ordinal };

/**
 * The line under a graded badge: "62nd percentile of 690 real superflex teams",
 * and the same extremes wording percentileClaim uses at the ends.
 *
 * 🔴 THE CELL IS PART OF THE CLAIM, NOT DECORATION. A percentile with no
 * population behind it is not a fact anybody can check, so the count and the
 * format are in the sentence rather than in a legend somewhere else on the
 * page. Null when there is no percentile: the caller says what is missing.
 */
export function percentileLine(pct, n, words, noun = "teams") {
  if (pct == null || !Number.isFinite(pct) || !n) return null;
  if (pct >= 100) return `beats all ${fmtCount(n)} real ${words} ${noun}`;
  if (pct <= 0) return `trails all ${fmtCount(n)} real ${words} ${noun}`;
  return `${ordinal(pctShown(pct))} percentile of ${fmtCount(n)} real ${words} ${noun}`;
}

/**
 * The bench version: "68th percentile of 1,240 real superflex bench RBs".
 *
 * @RED@ THE NOUN IS THE WHOLE POINT. A bench card used to be measured against
 * real teams' STARTER groups and said "teams", so it read as a claim about the
 * roster and landed at the floor for every bench alive. Now it is measured
 * against real BENCH PLAYERS, and the sentence has to say so or the reader has
 * no way to know the comparison changed.
 */
export function benchPercentileLine(pct, n, words, group) {
  return percentileLine(pct, n, words, `bench ${group}s`);
}

/**
 * Counts of `values` in METER_BARS equal-width buckets from min to max, plus
 * the fraction of the way across that axis a given value sits.
 *
 * A cell whose values are all identical has no axis at all: it returns one full
 * bucket and puts the team in the middle rather than dividing by zero.
 */
export function histogram(values, value) {
  const arr = values || [];
  if (arr.length === 0) return { bars: [], peak: 0, at: null };
  const min = arr[0];
  const max = arr[arr.length - 1];
  const span = max - min;
  const bars = new Array(METER_BARS).fill(0);
  const slot = (v) =>
    span <= 0
      ? 0
      : Math.max(0, Math.min(METER_BARS - 1, Math.floor(((v - min) / span) * METER_BARS)));
  for (const v of arr) bars[slot(v)] += 1;
  const peak = Math.max(...bars);
  const at =
    value == null || !Number.isFinite(value)
      ? null
      : span <= 0
        ? 0.5
        : Math.max(0, Math.min(1, (value - min) / span));
  return { bars, peak, at };
}

/**
 * One strip: the corpus as bars, the team as a marker, the percentile as a
 * number that counts up to it.
 *
 * @param values      the cell's ascending array — the real teams
 * @param value       this team's value on the same axis
 * @param percentile  where that lands, 0-100
 * @param heading     what the number is ("projection percentile")
 * @param caption     the sentence under the bars, counts already interpolated
 * @param claim       the extremes-aware sentence, or null
 * @param tone        the colour the number and the marker share
 * @param compact     the second strip is smaller than the first
 */
export default function CorpusMeter({
  values,
  value,
  percentile,
  heading,
  caption,
  claim,
  tone,
  compact = false,
  testId,
}) {
  const still = prefersReducedMotion();
  // Mount at zero and sweep, unless the viewer asked us not to move.
  const [swept, setSwept] = useState(still);
  // 🔴 THE COUNT-UP IS NOW SHARED (hooks/useCountUp.js), not reimplemented here.
  // Its easing is still the JS mirror of METER_LOOK.easing — a cubic ease-out is
  // the same shape as that bezier, so the number and the marker agree the whole
  // way rather than only at the two ends. The hook's header carries that pact;
  // the team report's headline reads the same loop.
  const shown = useCountUp(percentile, METER_LOOK.sweepMs, still);

  useEffect(() => {
    if (still) {
      setSwept(true);
      return undefined;
    }
    // The zero state paints first; changing the target on the next frame is
    // what gives the CSS transition something to interpolate from.
    const start = requestAnimationFrame(() => setSwept(true));
    return () => cancelAnimationFrame(start);
    // Once per report render. The report object is replaced when the roster is,
    // and nothing else should re-run the sweep.
  }, [percentile, still]);

  const { bars, peak, at } = histogram(values, value);
  if (bars.length === 0 || at === null) return null;

  const height = compact ? 26 : 40;

  return (
    <div data-testid={testId} style={{ marginTop: compact ? 12 : 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div
          data-testid={`${testId}-number`}
          style={{
            fontSize: compact ? 20 : 26,
            fontWeight: 900,
            lineHeight: 1.1,
            color: tone,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {pctShown(shown)}
        </div>
        <div style={{ fontSize: SZ.micro, color: C.text.faint }}>{heading}</div>
      </div>

      {claim && (
        <div style={{ fontSize: SZ.small, color: C.text.secondary, marginTop: 2 }}>{claim}</div>
      )}

      <div
        aria-hidden="true"
        style={{
          position: "relative",
          height,
          marginTop: 6,
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {bars.map((n, i) => (
          <div
            key={i}
            data-testid={`${testId}-bar-${i}`}
            style={{
              flex: 1,
              // A bucket with teams in it stays visible: the 2px floor keeps a
              // thin tail from reading as a hole in the corpus, and an empty
              // bucket gets a hairline so the axis is continuous.
              height: n === 0 ? 1 : Math.max(2, Math.round((n / peak) * height)),
              background: n === 0 ? C.border.subtle : C.border.default,
              borderRadius: 1,
              // Grown from the baseline, one after another across the strip.
              // scaleY rather than height so nothing relayouts mid-sweep, and
              // the height above stays the single source of the real value.
              transformOrigin: "bottom",
              transform: swept ? "scaleY(1)" : "scaleY(0)",
              transition: still
                ? "none"
                : `transform ${METER_LOOK.barRiseMs}ms ${METER_LOOK.easing} ${
                    i * METER_LOOK.barStaggerMs
                  }ms`,
            }}
          />
        ))}
        <div
          data-testid={`${testId}-marker`}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: 2,
            marginLeft: -1,
            left: `${(swept ? at : 0) * 100}%`,
            background: tone,
            borderRadius: 1,
            transition: still
              ? "none"
              : `left ${METER_LOOK.sweepMs}ms ${METER_LOOK.easing}`,
          }}
        />
      </div>

      <div style={{ fontSize: SZ.micro, color: C.text.faint, marginTop: 5, lineHeight: 1.4 }}>
        {caption}
      </div>
    </div>
  );
}
